import { useState, useEffect } from 'react';
import { 
  getUsers, 
  updateUserAccess,
  getUserHistory, 
  deleteUser, 
  toggleUserAdmin,
  getAllowedEmails,
  addAllowedEmail,
  deleteAllowedEmail,
  uploadAllowedEmails
} from '../lib/api';
import { formatDate, getPercentageColor, getPercentageBadge } from '../lib/utils';
import { getRemainingDays } from '../lib/access';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Users, 
  Shield, 
  Trash2, 
  History, 
  Upload, 
  Plus,
  Mail,
  Loader2,
  Search,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userHistory, setUserHistory] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [uploading, setUploading] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [accessUser, setAccessUser] = useState(null);
  const [remainingDays, setRemainingDays] = useState('');
  const [savingAccess, setSavingAccess] = useState(false);

  const handleSaveAccess = async (e) => {
    e.preventDefault();
    const days = Number(remainingDays);
    if (remainingDays.trim() === '' || !Number.isInteger(days) || days < 0 || days > 36500) {
      toast.error('Informe um número inteiro entre 0 e 36500 dias');
      return;
    }
    setSavingAccess(true);
    try {
      const response = await updateUserAccess(accessUser.id, days);
      setUsers(current => current.map(item => item.id === accessUser.id
        ? { ...item, access_expires_at: response.data.access_expires_at } : item));
      setAccessUser(null);
      toast.success('Prazo de acesso atualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar prazo de acesso');
    } finally {
      setSavingAccess(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, emailsRes] = await Promise.all([
        getUsers(),
        getAllowedEmails()
      ]);
      setUsers(usersRes.data);
      setAllowedEmails(emailsRes.data);
    } catch (err) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleViewHistory = async (user) => {
    setSelectedUser(user);
    setHistoryDialogOpen(true);
    
    try {
      const response = await getUserHistory(user.id);
      setUserHistory(response.data);
    } catch (err) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const handleToggleAdmin = async (user) => {
    try {
      await toggleUserAdmin(user.id);
      toast.success('Status de administrador alterado');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar status');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await deleteUser(selectedUser.id);
      toast.success('Usuário excluído');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.trim()) return;

    setSavingEmail(true);
    try {
      await addAllowedEmail(newEmail.trim());
      toast.success('E-mail adicionado');
      setEmailDialogOpen(false);
      setNewEmail('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao adicionar e-mail');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleDeleteEmail = async (email) => {
    try {
      await deleteAllowedEmail(email);
      toast.success('E-mail removido');
      loadData();
    } catch (err) {
      toast.error('Erro ao remover e-mail');
    }
  };

  const handleUploadEmails = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadAllowedEmails(file);
      toast.success(`${response.data.added} e-mails adicionados`);
      loadData();
    } catch (err) {
      toast.error('Erro ao fazer upload');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const getDateTime = (dateString) => {
    if (!dateString) return 0;
    const value = new Date(dateString).getTime();
    return Number.isNaN(value) ? 0 : value;
  };

  const filteredUsers = users
    .filter(u => u.email.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (a.is_admin !== b.is_admin) {
        return a.is_admin ? -1 : 1;
      }

      const lastAccessDiff = getDateTime(b.last_login_at) - getDateTime(a.last_login_at);
      if (lastAccessDiff !== 0) return lastAccessDiff;

      return getDateTime(b.created_at) - getDateTime(a.created_at);
    });

  const formatLastAccess = (dateString) => (
    dateString ? formatDate(dateString) : 'Nunca acessou'
  );

  return (
    <Layout>
      <div className="max-w-6xl mx-auto animate-fadeIn">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center">
            <Users className="w-6 h-6 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-zinc-900">Usuários</h1>
            <p className="text-zinc-500">Gerenciar usuários e permissões</p>
          </div>
        </div>

        <Tabs defaultValue="users" className="space-y-6">
          <TabsList>
            <TabsTrigger value="users" data-testid="tab-users">
              <Users className="w-4 h-4 mr-2" />
              Usuários ({users.length})
            </TabsTrigger>
            <TabsTrigger value="allowed" data-testid="tab-allowed">
              <Mail className="w-4 h-4 mr-2" />
              E-mails Permitidos ({allowedEmails.length})
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            {/* Search */}
            <div className="bg-white border border-zinc-200 rounded-xl p-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  placeholder="Buscar usuário..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-users"
                />
              </div>
            </div>

            {/* Users List */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
              </div>
            ) : (
              <div className="bg-white border border-zinc-200 rounded-xl overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>E-mail</th>
                      <th>Tipo</th>
                      <th>Cadastro</th>
                      <th>Último acesso</th>
                      <th>Dias restantes de acesso</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-zinc-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-zinc-600">
                                {user.email.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="font-medium">{user.email}</span>
                          </div>
                        </td>
                        <td>
                          {user.is_admin ? (
                            <span className="badge badge-info">
                              <Shield className="w-3 h-3 mr-1" />
                              Admin
                            </span>
                          ) : (
                            <span className="badge bg-zinc-100 text-zinc-600">
                              Aluno
                            </span>
                          )}
                        </td>
                        <td className="text-zinc-500">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="text-zinc-500">
                          {formatLastAccess(user.last_login_at)}
                        </td>
                        <td className="whitespace-nowrap" data-testid={`access-days-${user.id}`}>
                          {user.is_admin ? 'Sem limite' : (
                            user.access_expires_at || user.access_started_at || user.created_at
                              ? `${getRemainingDays(user.access_started_at || user.created_at, new Date(), user.access_expires_at)} dias`
                              : 'Data indisponível'
                          )}
                          {!user.is_admin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="ml-2"
                              data-testid={`edit-access-${user.id}`}
                              onClick={() => {
                                setAccessUser(user);
                                setRemainingDays(String(getRemainingDays(user.access_started_at || user.created_at, new Date(), user.access_expires_at)));
                              }}
                            >
                              Editar dias
                            </Button>
                          )}
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewHistory(user)}
                              data-testid={`view-history-${user.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Histórico
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleAdmin(user)}
                              data-testid={`toggle-admin-${user.id}`}
                            >
                              <Shield className="w-4 h-4 mr-1" />
                              {user.is_admin ? 'Remover Admin' : 'Tornar Admin'}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(user);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Allowed Emails Tab */}
          <TabsContent value="allowed">
            {/* Actions */}
            <div className="flex items-center gap-3 mb-6">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleUploadEmails}
                  className="hidden"
                  data-testid="upload-emails-input"
                />
                <Button variant="outline" disabled={uploading} asChild>
                  <span>
                    {uploading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Upload CSV
                  </span>
                </Button>
              </label>
              <Button 
                onClick={() => setEmailDialogOpen(true)}
                className="bg-zinc-900 hover:bg-zinc-800 text-white"
                data-testid="add-email-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar E-mail
              </Button>
            </div>

            {/* CSV Format Info */}
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-sky-800">
                <strong>Formato do CSV:</strong> email (uma coluna com o cabeçalho "email")
              </p>
            </div>

            {/* Emails List */}
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>E-mail</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {allowedEmails.map((item) => {
                    const isRegistered = users.some(u => u.email === item.email);
                    return (
                      <tr key={item.email}>
                        <td>
                          <div className="flex items-center gap-3">
                            <Mail className="w-4 h-4 text-zinc-400" />
                            <span>{item.email}</span>
                          </div>
                        </td>
                        <td>
                          {isRegistered ? (
                            <span className="badge badge-success">Cadastrado</span>
                          ) : (
                            <span className="badge badge-warning">Pendente</span>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteEmail(item.email)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-email-${item.email}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>

        <Dialog open={!!accessUser} onOpenChange={(open) => { if (!open && !savingAccess) setAccessUser(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar dias de acesso</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveAccess} className="space-y-4">
              <p className="text-sm text-zinc-500">{accessUser?.email}</p>
              <div className="space-y-2">
                <Label htmlFor="remaining-days">Dias restantes</Label>
                <Input id="remaining-days" type="number" min="0" max="36500" step="1" required
                  value={remainingDays} onChange={(e) => setRemainingDays(e.target.value)} disabled={savingAccess}
                  data-testid="remaining-days-input" />
                <p className="text-sm text-zinc-500">
                  Defina o total de dias a partir de agora. Por exemplo, para aumentar de 10 para 40 dias, informe 40. Zero encerra o acesso.
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" disabled={savingAccess} onClick={() => setAccessUser(null)}>Cancelar</Button>
                <Button type="submit" disabled={savingAccess} data-testid="save-access-btn">
                  {savingAccess ? 'Salvando...' : 'Salvar dias'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* History Dialog */}
        <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Histórico de {userHistory?.user?.email}
              </DialogTitle>
            </DialogHeader>

            {userHistory?.attempts?.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                Nenhum simulado realizado
              </div>
            ) : (
              <div className="space-y-3">
                {userHistory?.attempts?.map((attempt) => (
                  <div
                    key={attempt.id}
                    className="bg-zinc-50 border border-zinc-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{attempt.subject_name}</p>
                        <p className="text-sm text-zinc-500">
                          {formatDate(attempt.created_at)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`text-xl font-bold ${getPercentageColor(attempt.percentage)}`}>
                          {attempt.percentage.toFixed(1)}%
                        </p>
                        <p className="text-sm text-zinc-500">
                          {attempt.score}/{attempt.total_questions}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Email Dialog */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Adicionar E-mail Permitido</DialogTitle>
            </DialogHeader>

            <div className="py-4">
              <Label>E-mail</Label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="usuario@email.com"
                data-testid="new-email-input"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleAddEmail}
                disabled={savingEmail}
                className="bg-zinc-900 hover:bg-zinc-800 text-white"
                data-testid="confirm-add-email"
              >
                {savingEmail && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Adicionar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete User Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir Usuário</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir o usuário <strong>{selectedUser?.email}</strong>? 
                Todo o histórico de simulados será perdido.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteUser}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-user"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default AdminUsersPage;
